import { useState, type FormEvent } from 'react';
import { Card, Button } from '@/components/ui/primitives';
import { api, ApiError, USE_MOCK_AUTH } from '@/lib/api';

/* Settings › Security — change your own password (design-spec v1.1 §7.11).
 * Calls POST /v1/auth/change-password; the CSRF header is attached automatically
 * by the API client (double-submit from the bc_csrf cookie). On success the
 * backend rotates the session, so the browser stays signed in via fresh cookies.
 * Styling is token-driven and minimal — visual polish is deferred. */

const MIN_LENGTH = 10; // mirrors the backend strength floor

type Field = 'current' | 'next' | 'confirm';

function fieldError(field: Field, v: { current: string; next: string; confirm: string }): string | null {
  switch (field) {
    case 'current':
      return v.current === '' ? 'Enter your current password.' : null;
    case 'next':
      if (v.next === '') return 'Enter a new password.';
      if (v.next.length < MIN_LENGTH) return `Use at least ${MIN_LENGTH} characters.`;
      if (v.next === v.current && v.current !== '') return 'Choose a password different from the current one.';
      return null;
    case 'confirm':
      if (v.confirm === '') return 'Re-enter the new password.';
      if (v.confirm !== v.next) return 'Passwords do not match.';
      return null;
  }
}

export function ChangePasswordPanel() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState<Record<Field, boolean>>({
    current: false,
    next: false,
    confirm: false,
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; msg: string } | null>(null);

  const values = { current, next, confirm };
  const errors: Record<Field, string | null> = {
    current: fieldError('current', values),
    next: fieldError('next', values),
    confirm: fieldError('confirm', values),
  };
  const valid = !errors.current && !errors.next && !errors.confirm;

  function markTouched(f: Field) {
    setTouched((t) => ({ ...t, [f]: true }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched({ current: true, next: true, confirm: true });
    setStatus(null);
    if (!valid) return;

    if (USE_MOCK_AUTH) {
      setStatus({ kind: 'ok', msg: 'Demo mode — password change is simulated, not persisted.' });
      setCurrent('');
      setNext('');
      setConfirm('');
      setTouched({ current: false, next: false, confirm: false });
      return;
    }

    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      setStatus({ kind: 'ok', msg: 'Password changed. Sessions on your other devices were signed out.' });
      setCurrent('');
      setNext('');
      setConfirm('');
      setTouched({ current: false, next: false, confirm: false });
    } catch (err) {
      setStatus({ kind: 'error', msg: changeErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="max-w-md p-5">
      <h3 className="mb-1 text-sm font-medium text-text-primary">Change password</h3>
      <p className="mb-4 text-xs text-text-tertiary">
        Choose a strong password of at least {MIN_LENGTH} characters. Changing it signs out your
        other devices.
      </p>

      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        <PasswordField
          label="Current password"
          autoComplete="current-password"
          value={current}
          onChange={setCurrent}
          onBlur={() => markTouched('current')}
          error={touched.current ? errors.current : null}
        />
        <PasswordField
          label="New password"
          autoComplete="new-password"
          value={next}
          onChange={setNext}
          onBlur={() => markTouched('next')}
          error={touched.next ? errors.next : null}
        />
        <PasswordField
          label="Confirm new password"
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          onBlur={() => markTouched('confirm')}
          error={touched.confirm ? errors.confirm : null}
        />

        {status && (
          <p className={status.kind === 'ok' ? 'text-sm text-success' : 'text-sm text-danger'}>
            {status.msg}
          </p>
        )}

        <Button type="submit" variant="primary" disabled={busy || !valid} className="w-full">
          {busy ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </Card>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  onBlur,
  error,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  error: string | null;
  autoComplete: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-text-secondary">{label}</span>
      <input
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={error ? true : undefined}
        className="h-control w-full rounded-md border border-border-default bg-canvas px-3 text-sm outline-none focus:border-ai focus:ring-2 focus:ring-ai/30 aria-[invalid=true]:border-danger"
        placeholder="••••••••"
      />
      {error && <span className="mt-1 block text-xs text-danger">{error}</span>}
    </label>
  );
}

function changeErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'invalid_credentials':
        return 'Your current password is incorrect.';
      case 'weak_password':
        return `New password must be at least ${MIN_LENGTH} characters.`;
      case 'csrf_failed':
      case 'not_authenticated':
        return 'Your session expired. Please sign in again and retry.';
      case 'network_error':
        return 'Could not reach the server. Check your connection and try again.';
      default:
        return err.message || 'Could not change your password. Please try again.';
    }
  }
  return 'Could not change your password. Please try again.';
}
